import { useField, useFormikContext, Formik } from 'formik';
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { Form as Form$1 } from 'react-bootstrap';
import classnames from 'classnames';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var noop = function noop() {
  return null;
};

var GroupContext = /*#__PURE__*/createContext({});
var Group = function Group(_ref) {
  var label = _ref.label,
      helpText = _ref.helpText,
      error = _ref.error,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["label", "helpText", "error", "children"]);

  var _useField = useField(props),
      fieldError = _useField[1].error;

  return React.createElement(GroupContext.Provider, {
    value: {
      name: props.name
    }
  }, React.createElement(Form$1.Group, Object.assign({}, props), label && React.createElement(Form$1.Label, null, label), children, React.createElement(Form$1.Control.Feedback, {
    type: "invalid"
  }, error != null ? error : fieldError), helpText && React.createElement(Form$1.Text, {
    muted: true
  }, helpText)));
};

var Checkbox = function Checkbox(_ref) {
  var custom = _ref.custom,
      props = _objectWithoutPropertiesLoose(_ref, ["custom"]);

  var _useFormikContext = useFormikContext(),
      values = _useFormikContext.values,
      errors = _useFormikContext.errors,
      touched = _useFormikContext.touched,
      setFieldValue = _useFormikContext.setFieldValue,
      setFieldTouched = _useFormikContext.setFieldTouched;

  var _useContext = useContext(GroupContext),
      _useContext$name = _useContext.name,
      groupName = _useContext$name === void 0 ? "" : _useContext$name;

  var _useField = useField(props),
      _useField$ = _useField[0],
      name = _useField$.name,
      onBlur = _useField$.onBlur;

  var isChecked = useMemo(function () {
    return values[groupName].includes(name);
  }, [groupName, name, values]);
  var isInvalid = Boolean(errors[groupName]) && touched[groupName];
  var handleChange = useCallback(function (e) {
    props.onChange(e);
    setFieldTouched(groupName, true);
    setFieldValue(groupName, isChecked ? values[groupName].filter(function (value) {
      return value !== name;
    }) : [].concat(values[groupName], [name]));
  }, [groupName, isChecked, name, props, setFieldTouched, setFieldValue, values]);
  return React.createElement(Form$1.Check, Object.assign({}, props, {
    id: name,
    type: "checkbox",
    className: classnames({
      "is-invalid": isInvalid
    }),
    custom: custom
  }), React.createElement(Form$1.Check.Input, Object.assign({}, props, {
    type: "checkbox",
    checked: isChecked,
    isInvalid: isInvalid,
    onChange: handleChange,
    onBlur: onBlur
  })), React.createElement(Form$1.Check.Label, {
    title: props.title
  }, props.label));
};
Checkbox.defaultProps = {
  onChange: noop
};

function useChange(props) {
  var _useField = useField(props),
      onChange = _useField[0].onChange;

  return useCallback(function (e) {
    return props.onChange(e), onChange(e);
  }, [onChange, props.onChange]);
}

var Input = function Input(_ref) {
  var label = _ref.label,
      helpText = _ref.helpText,
      props = _objectWithoutPropertiesLoose(_ref, ["label", "helpText"]);

  var _useField = useField(props),
      _useField$ = _useField[0],
      name = _useField$.name,
      value = _useField$.value,
      onBlur = _useField$.onBlur,
      _useField$2 = _useField[1],
      error = _useField$2.error,
      touched = _useField$2.touched;

  var handleChange = useChange(props);
  return React.createElement(Group, {
    name: name,
    controlId: name,
    label: label,
    helpText: helpText,
    error: error
  }, React.createElement(Form$1.Control, Object.assign({}, props, {
    name: name,
    value: value == null ? void 0 : value.toString(),
    isInvalid: Boolean(error) && touched,
    onChange: handleChange,
    onBlur: onBlur
  })));
};
Input.defaultProps = {
  onChange: noop
};

var Radio = function Radio(_ref) {
  var custom = _ref.custom,
      props = _objectWithoutPropertiesLoose(_ref, ["custom"]);

  var _useFormikContext = useFormikContext(),
      values = _useFormikContext.values,
      errors = _useFormikContext.errors,
      touched = _useFormikContext.touched,
      setFieldValue = _useFormikContext.setFieldValue,
      setFieldTouched = _useFormikContext.setFieldTouched;

  var _useContext = useContext(GroupContext),
      _useContext$name = _useContext.name,
      groupName = _useContext$name === void 0 ? "" : _useContext$name;

  var _useField = useField(props),
      _useField$ = _useField[0],
      name = _useField$.name,
      onBlur = _useField$.onBlur;

  var isInvalid = Boolean(errors[groupName]) && touched[groupName];
  var handleChange = useCallback(function (e) {
    props.onChange(e);
    setFieldTouched(groupName, true);
    setFieldValue(groupName, name);
  }, [groupName, name, props.onChange, setFieldTouched, setFieldValue]);
  return React.createElement(Form$1.Check, Object.assign({}, props, {
    id: name,
    name: groupName,
    type: "radio",
    className: classnames({
      "is-invalid": isInvalid
    }),
    custom: custom
  }), React.createElement(Form$1.Check.Input, Object.assign({}, props, {
    type: "radio",
    checked: values[groupName] === name,
    isInvalid: isInvalid,
    onChange: handleChange,
    onBlur: onBlur
  })), React.createElement(Form$1.Check.Label, {
    title: props.title
  }, props.label));
};
Radio.defaultProps = {
  onChange: noop
};

var Range = function Range(props) {
  return React.createElement(Input, Object.assign({}, props, {
    type: "range"
  }));
};

var Select = function Select(_ref) {
  var label = _ref.label,
      helpText = _ref.helpText,
      placeholder = _ref.placeholder,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["label", "helpText", "placeholder", "children"]);

  var _useField = useField(props),
      _useField$ = _useField[0],
      name = _useField$.name,
      value = _useField$.value,
      onBlur = _useField$.onBlur,
      _useField$2 = _useField[1],
      error = _useField$2.error,
      touched = _useField$2.touched;

  var handleChange = useChange(props);
  return React.createElement(Group, {
    name: name,
    controlId: name,
    label: label,
    helpText: helpText,
    error: error
  }, React.createElement(Form$1.Control, Object.assign({}, props, {
    as: "select",
    name: name,
    value: value == null ? void 0 : value.toString(),
    isInvalid: Boolean(error) && touched,
    onChange: handleChange,
    onBlur: onBlur
  }), placeholder && React.createElement("option", {
    value: "",
    disabled: true
  }, placeholder), children));
};
Select.defaultProps = {
  onChange: noop
};

var Textarea = function Textarea(_ref) {
  var label = _ref.label,
      helpText = _ref.helpText,
      props = _objectWithoutPropertiesLoose(_ref, ["label", "helpText"]);

  var _useField = useField(props),
      _useField$ = _useField[0],
      name = _useField$.name,
      value = _useField$.value,
      onBlur = _useField$.onBlur,
      _useField$2 = _useField[1],
      error = _useField$2.error,
      touched = _useField$2.touched;

  var handleChange = useChange(props);
  return React.createElement(Group, {
    name: name,
    controlId: name,
    label: label,
    helpText: helpText,
    error: error
  }, React.createElement(Form$1.Control, Object.assign({}, props, {
    as: "textarea",
    name: name,
    value: value == null ? void 0 : value.toString(),
    isInvalid: Boolean(error) && touched,
    onChange: handleChange,
    onBlur: onBlur
  })));
};
Textarea.defaultProps = {
  onChange: noop
};

var Form = function Form(_ref) {
  var className = _ref.className,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "children"]);

  return React.createElement(Formik, Object.assign({}, props), function (_ref2) {
    var handleSubmit = _ref2.handleSubmit;
    return React.createElement(Form$1, {
      className: className,
      onSubmit: handleSubmit
    }, children);
  });
};
Form.Group = Group;
Form.Input = Input;
Form.Textarea = Textarea;
Form.Select = Select;
Form.Checkbox = Checkbox;
Form.Radio = Radio;
Form.Range = Range;

export { Form };
//# sourceMappingURL=react-bootstrap-formik.esm.js.map
